import React, { useEffect, useLayoutEffect } from 'react';
import { ThemeProvider } from '@cognitiv/cyprus-ui';
import { useAppSelector } from 'ducks/hooks';
import { getLookups } from 'ducks/lookups/operators';
import { selectUser } from 'ducks/user/selectors';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { routes } from 'routes';
import { PrivateTemplate } from 'templates/PrivateTemplate';
import { PublicTemplate } from 'templates/PublicTemplate';
import { handleError } from 'utils/errors';

import { Suspense } from 'Suspense';

export const App = () => {
  const { theme, is_dark_theme, setLocalStorageTheme } = useSwitchTheme();
  const user = useAppSelector(selectUser);

  useLayoutEffect(() => {
    if (is_dark_theme !== null) {
      setLocalStorageTheme(is_dark_theme);
    } else {
      setLocalStorageTheme(false);
    }
  }, [is_dark_theme, setLocalStorageTheme]);

  useEffect(() => {
    const { private_key, public_key, email_address } = user;
    const getApp = async () => {
      try {
        if (private_key && public_key) {
          await getLookups();
        }
      } catch (err) {
        const error = err as Error;
        handleError({ ...error, email_address });
      }
    };
    getApp();
  }, [user]);

  const {
    ALL,
    ROOT,
    LOGIN,
    HEALTH,
    PROJECTS,
    BATCHES,
    BATCH_DEFINITIONS,
    BATCH_DEFINITION,
    SCHEDULED_BATCHES,
    SCHEDULED_BATCH,
    JOBS,
    JOB,
    JOB_DEFINITIONS,
    JOB_DEFINITION,
  } = routes;

  return (
    <BrowserRouter basename="/">
      <ThemeProvider theme={theme}>
        <Routes>
          <Route path="" index element={<Navigate to={PROJECTS.path} />} />
          <Route path={HEALTH.path} element={<Suspense element={HEALTH.element} />} />
          <Route path={ROOT.path} element={<PublicTemplate />}>
            <Route path={LOGIN.path} element={<Suspense element={LOGIN.element} />} />
          </Route>
          <Route path={ROOT.path} element={<PrivateTemplate />}>
            <Route path={PROJECTS.path} element={<Suspense element={PROJECTS.element} />} />
            <Route path={JOBS.path} element={<Suspense element={JOBS.element} />} />
            <Route path={JOB_DEFINITIONS.path} element={<Suspense element={JOB_DEFINITIONS.element} />} />
            <Route path={JOB_DEFINITION.path} element={<Suspense element={JOB_DEFINITION.element} />} />
            <Route path={JOB.path} element={<Suspense element={JOB.element} />} />
            <Route path={BATCHES.path} element={<Suspense element={BATCHES.element} />} />
            <Route path={SCHEDULED_BATCHES.path} element={<Suspense element={SCHEDULED_BATCHES.element} />} />
            <Route path={SCHEDULED_BATCH.path} element={<Suspense element={SCHEDULED_BATCH.element} />} />
            <Route path={BATCH_DEFINITIONS.path} element={<Suspense element={BATCH_DEFINITIONS.element} />} />
            <Route path={BATCH_DEFINITION.path} element={<Suspense element={BATCH_DEFINITION.element} />} />
            <Route path={ALL.path} element={<Suspense element={ALL.element} />} />
          </Route>
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
};
